<template>
  <div>
    <b-modal
      id="edit-catalog-modal"
      title="Edit Flipbook"
      centered
      ok-title="Save"
      :ok-disabled="!canSubmit || isSaving"
      @show="populateForm"
      @ok="accept"
    >
      <b-form-group
        label="Name"
        label-for="edit-catalog-name-input"
        invalid-feedback="Enter a name of at least 1 and less than 255 chars."
        :state="nameState"
      >
        <b-form-input
          id="edit-catalog-name-input"
          v-model="name"
          :state="nameState"
          required
          autofocus
          trim
        />
      </b-form-group>

      <div class="brand-section mt-3">
        <div class="brand-header">
          <span class="brand-label">Brand</span>
          <label class="brand-default-check">
            <input
              v-model="setAsDefault"
              type="checkbox"
            >
            <span>Set as default</span>
          </label>
        </div>

        <div class="brand-row">
          <div class="logo-box">
            <span v-if="!logoUrl">LOGO</span>
            <img
              v-else
              :src="logoUrl"
              alt="Logo preview"
            >
          </div>

          <button
            type="button"
            class="tb-action"
            @click="triggerLogoFileInput"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 19V5" />
              <path d="M5 12l7-7 7 7" />
            </svg>
            Upload
          </button>

          <button
            type="button"
            class="tb-action"
            @click="openFileBrowser"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
            </svg>
            File Browser
          </button>

          <div
            v-click-outside="closePicker"
            class="brand-color-wrap"
          >
            <button
              type="button"
              class="brand-color-swatch"
              :style="{ backgroundColor: brandColor }"
              aria-label="Choose brand color"
              @click="pickerOpen = !pickerOpen"
            />

            <ColorPickerPopover
              v-if="pickerOpen"
              v-model="brandColor"
              class="brand-color-popover"
            />
          </div>
        </div>

        <input
          ref="logoFileInput"
          type="file"
          accept="image/*"
          class="d-none"
          @change="onLogoFileSelected"
        >
      </div>

      <b-form-group class="mt-3 catalog-cover-upload">
        <input
          ref="coverImageInput"
          type="file"
          accept="image/*"
          class="d-none"
          @change="onCoverImageChange"
        >

        <div class="catalog-cover-upload__content d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center catalog-cover-upload__status">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-primary catalog-cover-upload__icon-info"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
              <path d="M4 17v2" />
              <path d="M5 18H3" />
            </svg>

            <span class="font-sm">
              {{ coverStatus }}
            </span>
          </div>

          <button
            type="button"
            class="btn btn-link p-0 font-sm"
            @click="$refs.coverImageInput.click()"
          >
            <i class="bi bi-upload mr-1 catalog-cover-upload__icon-upload" />
            {{ hasCustomCover ? 'Replace' : 'Upload custom' }}
          </button>
        </div>
      </b-form-group>

      <div
        role="group"
        class="mt-3"
      >
        <div class="d-flex align-items-center justify-content-between">
          <label class="mb-0">Password</label>

          <b-form-checkbox
            v-model="requirePassword"
            class="mr-3"
          >
            Require password to view
          </b-form-checkbox>
        </div>

        <b-form-input
          v-model="password"
          class="mt-2"
          :state="passwordState"
          :disabled="!requirePassword"
          placeholder="New password"
          type="password"
        />

        <small
          v-if="catalogIsProtected && requirePassword && !password"
          class="form-text text-muted"
        >
          Leave empty to keep the current password.
        </small>

        <b-form-invalid-feedback v-if="requirePassword">
          Password must be at least 4 characters.
        </b-form-invalid-feedback>
      </div>
    </b-modal>

    <SelectFolderModal
      mode="select"
      modal-id="edit-catalog-logo-file-browser-modal"
      modal-title="Select logo"
      @onSelectedItems="onSelectedItems"
    />
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import CatalogService from '@frontend/services/api/catalogs';
import ColorPickerPopover from '../ColorPickerPopover.vue';
import SelectFolderModal from '@frontend/components/modules/file-browser/modals/SelectFolderModal.vue';

const DEFAULT_BRAND_COLOR = '#111725';

export default {
  name: 'EditCatalogModal',
  components: {
    ColorPickerPopover,
    SelectFolderModal,
  },
  directives: {
    ClickOutside,
  },
  props: {
    catalog: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    name: '',
    password: '',
    requirePassword: false,
    coverImage: null,
    hasExistingCover: false,
    setAsDefault: false,
    logoUrl: null,
    logoFile: null,
    logoObjectUrl: null,
    brandColor: DEFAULT_BRAND_COLOR,
    pickerOpen: false,
    isSaving: false,
  }),
  computed: {
    nameState() {
      return this.name.length > 0 && this.name.length <= 255;
    },
    catalogIsProtected() {
      if (this.catalog.isProtected !== undefined) {
        return this.asBoolean(this.catalog.isProtected);
      }

      return Boolean(this.catalog.password);
    },
    passwordState() {
      if (!this.requirePassword) {
        return null;
      }

      if (this.catalogIsProtected && !this.password) {
        return null;
      }

      return this.password.length >= 4 && this.password.length <= 255;
    },
    canSubmit() {
      return this.nameState && this.passwordState !== false;
    },
    hasCustomCover() {
      return Boolean(this.coverImage || this.hasExistingCover);
    },
    coverStatus() {
      if (this.coverImage) {
        return 'Cover will be generated from the new custom upload';
      }

      if (this.hasExistingCover) {
        return 'Current custom cover will be retained';
      }

      return 'Cover auto-generated from first layout';
    },
  },
  watch: {
    catalog: {
      immediate: true,
      handler() {
        this.populateForm();
      },
    },
    requirePassword(required) {
      if (!required) {
        this.password = '';
      }
    },
  },
  beforeDestroy() {
    this.revokeLogoObjectUrl();
  },
  methods: {
    asBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },
    populateForm() {
      this.name = this.catalog.name || '';
      this.password = '';
      this.requirePassword = this.catalogIsProtected;
      this.coverImage = null;
      this.hasExistingCover = Boolean(this.catalog.coverImage || this.catalog.cover_image);
      this.setAsDefault = this.asBoolean(
        this.catalog.setAsDefault ?? this.catalog.is_brand_default,
      );
      this.brandColor = this.catalog.brandColor
        || this.catalog.brand_color
        || DEFAULT_BRAND_COLOR;
      this.logoFile = null;
      this.revokeLogoObjectUrl();
      this.logoUrl = this.normalizeLogoUrl(
        this.catalog.logoUrl || this.catalog.brand_logo,
      );
      this.pickerOpen = false;

      if (this.$refs.logoFileInput) {
        this.$refs.logoFileInput.value = '';
      }

      if (this.$refs.coverImageInput) {
        this.$refs.coverImageInput.value = '';
      }
    },
    normalizeLogoUrl(value) {
      if (!value) {
        return null;
      }

      if (
        value.startsWith('http://')
        || value.startsWith('https://')
        || value.startsWith('/share?file=')
      ) {
        return value;
      }

      return `/share?file=${encodeURIComponent(value)}`;
    },
    async accept(event) {
      event.preventDefault();

      if (!this.canSubmit || this.isSaving) {
        return;
      }

      this.isSaving = true;

      try {
        const formData = new FormData();

        formData.append('name', this.name);
        formData.append('password', this.requirePassword ? this.password : '');
        formData.append(
          'removePassword',
          this.catalogIsProtected && !this.requirePassword ? '1' : '0',
        );
        formData.append('brandColor', this.brandColor);
        formData.append('setAsDefault', this.setAsDefault ? '1' : '0');

        if (this.coverImage) {
          formData.append('coverImage', this.coverImage);
        }

        if (this.logoFile) {
          formData.append('logoFile', this.logoFile);
        }

        await CatalogService.update(this.catalog.id, formData);

        this.$bvModal.hide('edit-catalog-modal');
        this.$emit('catalogUpdated');
      } catch (error) {
        toastr.error('Something went wrong while editing the Flipbook');
      } finally {
        this.isSaving = false;
      }
    },
    triggerLogoFileInput() {
      this.$refs.logoFileInput.click();
    },
    onLogoFileSelected(event) {
      const [file] = event.target.files;

      this.setLogoFile(file || null);
    },
    setLogoFile(file) {
      this.logoFile = file;
      this.revokeLogoObjectUrl();

      if (file) {
        this.logoObjectUrl = URL.createObjectURL(file);
        this.logoUrl = this.logoObjectUrl;
      }
    },
    revokeLogoObjectUrl() {
      if (this.logoObjectUrl) {
        URL.revokeObjectURL(this.logoObjectUrl);
        this.logoObjectUrl = null;
      }
    },
    openFileBrowser() {
      this.$bvModal.show('edit-catalog-logo-file-browser-modal');
    },
    closePicker() {
      this.pickerOpen = false;
    },
    async onSelectedItems([item] = []) {
      if (!item || item.type === 'folder') {
        return;
      }

      const path = item.original_path || item.path;

      if (!path) {
        toastr.error(`Failed to process file: ${item.name}`);
        return;
      }

      const fileUrl = `${window.location.origin}/share?file=${encodeURIComponent(path)}`;

      try {
        const response = await fetch(fileUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();
        const file = new File([blob], item.name, { type: blob.type });

        this.setLogoFile(file);
      } catch (error) {
        console.error('Error processing file from browser:', error);
        toastr.error(`Failed to process file: ${item.name}`);
      }
    },
    onCoverImageChange(event) {
      const [file] = event.target.files;

      this.coverImage = file || null;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-header {
  padding-bottom: 0;
  border: none;
}

::v-deep .modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1;
}

::v-deep label,
::v-deep .custom-control-label {
  font-size: .875rem;
  font-weight: 500;
}

::v-deep .form-control,
::v-deep .form-control::placeholder {
  font-size: .875rem;
  line-height: 1.25rem;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .5rem;
}

.brand-label {
  font-size: .875rem;
  font-weight: 500;
}

.brand-default-check {
  display: flex;
  align-items: center;
  gap: .375rem;
  margin: 0;
  cursor: pointer;
  user-select: none;

  input {
    width: 15px;
    height: 15px;
    accent-color: #16a34a;
    cursor: pointer;
  }
}

.brand-row {
  display: flex;
  align-items: center;
  gap: .625rem;
  padding: .625rem .75rem;
  border: 1px solid #e5e7eb;
  border-radius: .625rem;
}

.logo-box {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border: 1px dashed #d1d5db;
  border-radius: .5rem;
  background: #fafafa;
  color: #9ca3af;
  font-size: .6rem;
  font-weight: 600;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.tb-action {
  display: flex;
  align-items: center;
  gap: .375rem;
  padding: .375rem .25rem;
  border: none;
  background: transparent;
  color: #374151;
  font-size: .85rem;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #111827;
  }
}

.brand-color-wrap {
  position: relative;
  margin-left: auto;
}

.brand-color-swatch {
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, .1);
  border-radius: .5rem;
  cursor: pointer;
}

.brand-color-popover {
  position: absolute;
  z-index: 1000;
  top: calc(100% + .5rem);
  right: 0;
}

.catalog-cover-upload {
  padding: .5rem .75rem;
  border-radius: .375rem;
  background-color: rgba(241, 245, 249, .4);

  &__content,
  &__status {
    gap: .75rem;
  }

  &__icon-info {
    width: .875rem;
    height: .875rem;
    flex: none;
  }

  &__icon-upload {
    width: .75rem;
    height: .75rem;
  }
}
</style>
